import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderDetailsCard } from '@components/OrderProcessing/OrderDetailsCard';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useToast } from '@contexts/Toast';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  errorMessage,
  InvocieCrumbSteps,
  Length,
  OrderProcessingMasterData,
  OrderProcessStatus,
  timers,
  WeightType,
} from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { FrontendRoutes, ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  OrderDetailsCardInfo,
  OrderDetailsResponse,
  OrderProductNotes,
  OrderServiceNotes,
} from '@helpers/Models';
import { Axios } from '@helpers/ApiService';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OrderNotesModal } from '@components/Modal/OrderProcessing/OrderNotesModal';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';

export const OrderedItemsList: React.FC = () => {
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const { state } = useLocation();
  const orderId = state?.orderId;
  const toggleAccordion = (index: number): void => {
    setOpenAccordions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse>();
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const { showConfirmationToast } = UseConfirmationToast();
  const [isInvoiceGenerate, setInvoiceGenerate] = useState<boolean>(false);

  /**
   * Effect hook that runs when `orderId` changes.
   *
   * - If `orderId` is defined:
   *   - Fetches invoice order details via `fetchOrderDetails()`.
   * - If `orderId` is undefined:
   *   - Displays an "Invalid request" error toast.
   *   - Navigates back to the invoice list after a 4-second delay.
   *
   * @dependency orderId - Triggers the effect when the orderId ID changes.
   */
  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    } else {
      showToastMessage(errorMessage.INVALID_REQUEST, 'error');
      setTimeout(() => {
        navigate(FrontendRoutes.INVOICE_LIST);
      }, timers.TOAST_AUTO_CLOSE); //4000ms
    }
  }, [orderId]);

  /**
   * Fetches invoice details  from the API.
   *
   * Makes a POST request to the `INVOICE_DETAILS` endpoint
   * to retrieve the packaging master data.
   * On success, updates the `orderDetails` state with the response data.
   * On failure, shows an error toast message.
   */
  const fetchOrderDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.INVOICE_DETAILS, {
        id: orderId,
      });
      setOrderDetails(response.data.data);
      // If the orderStatus is less than or equal to 6(Final Invoice), set isInvoiceGenerated as true
      // for handling it's view.
      if (
        response.data.data?.orderInfo?.orderStatus &&
        response.data.data.orderInfo.orderStatus >= 6
      ) {
        setInvoiceGenerate(true);
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  // Order Details Card Info
  const orderDetailsCardInfo = {
    orderNumber: orderDetails?.orderInfo.orderNumber,
    createdByUser: orderDetails?.orderInfo.createdByUser,
    roles: orderDetails?.orderInfo.roles,
    customerName: orderDetails?.orderInfo.customerName,
    orderDate: orderDetails?.orderInfo.orderDate,
  } as OrderDetailsCardInfo;

  // Order Info Details for passing it to next screen
  const OrderInfoDetails = {
    orderId: orderDetails?.orderInfo.orderId,
    orderNumber: orderDetails?.orderInfo.orderNumber,
    customerName: orderDetails?.orderInfo.customerName,
    roles: orderDetails?.orderInfo.roles,
    createdBy: orderDetails?.orderInfo.createdByUser,
    createdDate: orderDetails?.orderInfo.orderDate,
  };

  /**
   * Generates an invoice for the current order by sending a POST request
   * to the invoice generation API endpoint.
   *
   * On success:
   * - Displays a confirmation toast with the server's response message.
   * - Navigates to the "All Invoices" page when the toast action is triggered.
   *
   * On failure:
   * - Displays an error toast indicating invoice generation failure.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves when the process completes.
   */
  const handleGenerateInvoice = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.INVOICE_GENERATION, {
        id: orderId,
      });
      showConfirmationToast(
        response.data.message,
        'Success', // title
        'success', // type
        () =>
          navigate(FrontendRoutes.ALL_INVOICES, { state: OrderInfoDetails }),
        'View All Invoices',
        () => fetchOrderDetails(),
        'Cancel',
      );
    } catch (error) {
      showToastMessage(errorMessage.ERROR_GENERATING_INVOICE, 'error');
    }
  };

  /**
   * Navigates the user to the "All Invoices" page with relevant order details.
   *
   * @function handleViewAllInvoices
   * @returns {void}
   *
   */
  const handleViewAllInvoices = (): void => {
    navigate(FrontendRoutes.ALL_INVOICES, { state: OrderInfoDetails });
  };

  const BreadCrumbsProps = {
    steps: InvocieCrumbSteps,
    activeItem: 'Ordered Items -',
    orderNumber: orderDetails?.orderInfo?.orderNumber,
    type: OrderProcessingMasterData.INVOICE,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={6} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          {/* Title section */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                Ordered Items
              </h2>
            </div>
            {/* Display View all invoice and Generate invoice when the order is invoiceable - isInvoiceGenerate as false */}
            {!isInvoiceGenerate && (
              <div className="flex items-center justify-between space-x-4">
                <button
                  onClick={handleViewAllInvoices}
                  type="button"
                  className="text-primary px-2 py-2 font-bold whitespace-nowrap"
                >
                  View all invoices
                </button>
                <button
                  type="submit"
                  onClick={handleGenerateInvoice}
                  //Disabling Generate invoice when invoiceable is 0 - order is not ready for generating invoice
                  disabled={orderDetails?.orderInfo?.invoiceable === 0}
                  className="w-full px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary 
                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  <span>Generate invoice</span>
                </button>
              </div>
            )}
          </div>
          {/* Order details card */}
          {orderDetails && (
            <OrderDetailsCard orderDetailsCardInfo={orderDetailsCardInfo} />
          )}
          {/* If all invoices of an order is completed, show success div */}
          {isInvoiceGenerate ? (
            <>
              <div className="flex-1 flex items-center justify-center mt-28">
                <div className="w-full max-w-md flex flex-col justify-start items-center gap-3 px-4">
                  <img
                    src={Assets.INVOICE_GENERATION_COMPLETE}
                    alt="invoice_complete_success"
                    className="w-[189px] h-[189px] object-contain"
                  />
                  <h2 className="text-center text-[#2A2960] text-3xl font-bold font-manrope leading-normal">
                    Congratulations!
                  </h2>
                  <div className="flex flex-col justify-center items-center gap-2.5 w-full">
                    <h5 className="text-center justify-center">
                      You have successfully generated all invoices
                    </h5>
                    <button
                      onClick={handleViewAllInvoices}
                      type="button"
                      className="w-full rounded-md mt-6 px-6 py-2 bg-[#004834] text-white"
                    >
                      View all Invoices
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {orderDetails?.species.map((speciesInfo, speciesIndex) => (
                <div key={speciesIndex}>
                  <div className="border rounded-md shadow-sm mb-3 mt-4">
                    <button
                      type="button"
                      onClick={() => toggleAccordion(speciesIndex)}
                      className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg focus:outline-primary"
                    >
                      <span>
                        {speciesInfo.speciesTag}
                        {speciesInfo.checkIn && speciesInfo.checkIn.inches && (
                          <>
                            {''} ({speciesInfo.checkIn.feet} {Length.FEET}{' '}
                            {speciesInfo.checkIn.inches} {Length.INCHES})
                          </>
                        )}
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setShowNotesModal(true)}
                          type="button"
                          className="text-primary text-sm px-2 font-bold whitespace-nowrap"
                        >
                          View notes
                        </button>
                        <div className="flex items-center gap-4">
                          <FontAwesomeIcon
                            className="text-[#000A16]"
                            icon={
                              openAccordions.includes(speciesIndex)
                                ? faChevronUp
                                : faChevronDown
                            }
                          />
                        </div>
                      </div>
                      {showNotesModal && (
                        <OrderNotesModal
                          speciesName={speciesInfo?.speciesTag ?? ''}
                          onClose={() => setShowNotesModal(false)}
                          checkInNotes={
                            speciesInfo?.checkIn.notes != ''
                              ? (speciesInfo?.checkIn.notes ?? '')
                              : errorMessage.NO_NOTES_FOUND
                          }
                          AdditionalNotes={
                            speciesInfo?.notes != ''
                              ? (speciesInfo?.notes ?? '')
                              : errorMessage.NO_NOTES_FOUND
                          }
                          ServiceNotes={speciesInfo.services.map(
                            (service): OrderServiceNotes => ({
                              orderServiceId: service.serviceId,
                              serviceName: service.name ?? '',
                              note: service.serviceProcessNotes ?? '',
                            }),
                          )}
                          ProductNotes={speciesInfo.products.map(
                            (product): OrderProductNotes => ({
                              orderProductId: product.productId,
                              productName: product.name ?? '',
                              note: product.productProcessNotes ?? '',
                            }),
                          )}
                        />
                      )}
                    </button>
                    <div>
                      {/* Accordion content */}
                      {openAccordions.includes(speciesIndex) && (
                        <div>
                          {/* Services section - only if services exist */}
                          {speciesInfo?.services?.length > 0 && (
                            <div>
                              <p className="px-3 text-lg font-semibold">
                                Services
                              </p>
                              <div>
                                {speciesInfo.services.map((service) => (
                                  <div
                                    key={service.serviceId}
                                    className="p-3 rounded-md flex items-center gap-4"
                                  >
                                    <img
                                      src={speciesInfo.imageUrl}
                                      alt={speciesInfo.name}
                                      className="w-16 h-16 object-cover rounded-md"
                                    />
                                    <div className="flex-1">
                                      <div className="flex justify-between items-center">
                                        <p className="text-lg">{service.name}</p>
                                        <span className="text-sm">
                                          <OrderStatusBadge
                                            status={service.status}
                                            type="serviceStatus"
                                          />
                                        </span>
                                      </div>
                                      {/* Display the following label, if service processing is not completed */}
                                      {service.status <
                                        OrderProcessStatus.COMPLETED && (
                                        <div className="bg-[#F5F5F5] p-2 rounded-md text-sm inline-block">
                                          {service.flatFeeFlag === 0
                                            ? 'Final Price to be calculated after processing'
                                            : 'Flat Fee'}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Products section - only if products exist */}
                          {speciesInfo?.products?.length > 0 && (
                            <div>
                              <p className="px-3 text-lg font-semibold">
                                Products and Packagings
                              </p>
                              {speciesInfo.products.map((product) => (
                                <div
                                  key={product.productId}
                                  className="p-3 rounded-md flex items-center gap-4"
                                >
                                  <div className="flex-1">
                                    <div className="flex justify-between items-center">
                                      <p className="text-[#1C1B1F]">
                                        {product.name}
                                        {product.fatPercent > 0 &&
                                          `, ${product.fatPercent}% Fat`}
                                      </p>
                                      <span className="text-sm">
                                        <OrderStatusBadge
                                          type="productStatus"
                                          status={product.status}
                                        />
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center mt-3">
                                      {/*  Display up to the first two add-ons. If there are more than two, append ", 
                                                                    ..." to indicate additional addons. */}
                                      {product.addons?.length > 0 && (
                                        <p className="text-[#43474E] mt-2 text-sm">
                                          {product.addons
                                            .slice(0, 2)
                                            .map((addon, idx) => (
                                              <span key={addon.addonId}>
                                                {addon.name}
                                                {idx <
                                                  product.addons.slice(0, 2)
                                                    .length -
                                                    1 && ', '}
                                              </span>
                                            ))}
                                          {product.addons.length > 2 &&
                                            ', ...'}
                                        </p>
                                      )}
                                      <span className="font-semibold bg-[#F5F5F5] text-[#43474E] py-2 px-3 min-w-[100px] inline-block text-center ml-auto">
                                        {product.status ==
                                        OrderProcessStatus.COMPLETED // product processing Completed
                                          ? product.finalQuantity
                                          : product.quantity}
                                        {product.productType === 1
                                          ? ` ${WeightType.LBS}`
                                          : ''}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};